// Web MIDI input (D11). Requests access after a user gesture, forwards note
// on/off (and mod wheel) to the supplied handlers, and degrades gracefully where
// Web MIDI is unavailable (Safari/Firefox). Minimal local types so we don't need
// @types/webmidi.

export interface MidiHandlers {
  noteOn: (midi: number, velocity: number) => void;
  noteOff: (midi: number) => void;
  /** CC1 mod wheel, 0..1 (optional). */
  modWheel?: (value: number) => void;
  /** Pitch bend, -1..1 (optional). */
  pitchBend?: (value: number) => void;
}

interface MidiMessageEvent {
  data: Uint8Array;
}
interface MidiInput {
  name?: string;
  onmidimessage: ((e: MidiMessageEvent) => void) | null;
}
interface MidiAccess {
  inputs: Map<string, MidiInput>;
  onstatechange: (() => void) | null;
}
type RequestMIDIAccess = () => Promise<MidiAccess>;

export interface MidiInitResult {
  available: boolean;
  message: string;
}

function handleMessage(data: Uint8Array, h: MidiHandlers): void {
  const status = data[0]! & 0xf0;
  const d1 = data[1] ?? 0;
  const d2 = data[2] ?? 0;
  switch (status) {
    case 0x90: // note on (velocity 0 == note off)
      if (d2 > 0) h.noteOn(d1, d2 / 127);
      else h.noteOff(d1);
      break;
    case 0x80: // note off
      h.noteOff(d1);
      break;
    case 0xb0: // control change
      if (d1 === 1) h.modWheel?.(d2 / 127);
      break;
    case 0xe0: // pitch bend (14-bit, centred at 8192)
      h.pitchBend?.((((d2 << 7) | d1) - 8192) / 8192);
      break;
    default:
      break;
  }
}

export async function initMidi(handlers: MidiHandlers): Promise<MidiInitResult> {
  const req = (navigator as Navigator & { requestMIDIAccess?: RequestMIDIAccess })
    .requestMIDIAccess;
  if (typeof req !== 'function') {
    return { available: false, message: 'MIDI: unsupported' };
  }
  try {
    const access = await req.call(navigator);
    const attach = (): void => {
      access.inputs.forEach((input) => {
        input.onmidimessage = (e) => handleMessage(e.data, handlers);
      });
    };
    attach();
    access.onstatechange = attach;
    const count = access.inputs.size;
    return {
      available: true,
      message: count > 0 ? `MIDI: ${count} input(s)` : 'MIDI: ready (no devices)',
    };
  } catch {
    return { available: false, message: 'MIDI: blocked' };
  }
}
